import ScreenRotationAltIcon from "@mui/icons-material/ScreenRotationAlt";
import {Box, IconButton, Slider, Stack, Typography} from "@mui/material";
import React, {
    forwardRef,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import {PrintPreviewData} from "../../hooks/useFloorplanPrint";
import {Orientation} from "../../utils/print-utils";
import {
    buildPreviewDrawRect,
    buildPreviewFitRect,
    buildPreviewPanBounds,
    buildPreviewViewportSize,
    clampPreviewPan,
    DEFAULT_PRINT_PREVIEW_STATE,
    getPreviewOffsetFromPan,
    getPreviewPanFromOffset,
    PrintPreviewCanvasState
} from "../../utils/print-preview.utils";

export interface PrintPreviewCanvasHandle {
    reset: (nextState?: PrintPreviewCanvasState) => void;
    getState: () => PrintPreviewCanvasState;
}

interface Props {
    open: boolean;
    pageOrientation: Orientation;
    previewData: PrintPreviewData | null;
    initialState?: PrintPreviewCanvasState;
    initialStateVersion?: number;
    onStateChange?: (state: PrintPreviewCanvasState) => void;
}

const PrintPreviewCanvas = forwardRef<PrintPreviewCanvasHandle, Props>(({
    open,
    pageOrientation,
    previewData,
    initialState,
    initialStateVersion = 0,
    onStateChange,
}, ref) => {
    const {t} = useTranslation();
    const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const viewportHostRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef<{startX: number; startY: number; panX: number; panY: number} | null>(null);
    const [viewportSize, setViewportSize] = useState({width: 0, height: 0});
    const [sourceImage, setSourceImage] = useState<HTMLImageElement | null>(null);
    const [imageNaturalSize, setImageNaturalSize] = useState({width: 0, height: 0});
    const [isDragging, setIsDragging] = useState(false);
    const [previewState, setPreviewState] = useState<PrintPreviewCanvasState>(DEFAULT_PRINT_PREVIEW_STATE);
    const previewStateRef = useRef<PrintPreviewCanvasState>(DEFAULT_PRINT_PREVIEW_STATE);
    const canPan = previewState.printScale > 1;
    const resolvedInitialState = initialState ?? DEFAULT_PRINT_PREVIEW_STATE;

    const updatePreviewState = (nextState: SetStateAction<PrintPreviewCanvasState>) => {
        setPreviewState((current) => {
            const resolvedState = typeof nextState === 'function'
                ? (nextState as (prevState: PrintPreviewCanvasState) => PrintPreviewCanvasState)(current)
                : nextState;
            previewStateRef.current = resolvedState;
            return resolvedState;
        });
    };

    useImperativeHandle(ref, () => ({
        reset: (nextState = resolvedInitialState) => {
            previewStateRef.current = nextState;
            setPreviewState(nextState);
            dragStateRef.current = null;
            setIsDragging(false);
        },
        getState: () => previewStateRef.current,
    }), [resolvedInitialState]);

    useEffect(() => {
        if (!open) {
            return;
        }
        previewStateRef.current = resolvedInitialState;
        setPreviewState(resolvedInitialState);
        dragStateRef.current = null;
        setIsDragging(false);
    }, [open, pageOrientation, previewData?.dataUrl, initialStateVersion]);

    useEffect(() => {
        onStateChange?.(previewState);
    }, [onStateChange, previewState]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const updateViewportSize = () => {
            const hostWidth = viewportHostRef.current?.clientWidth ?? 0;
            const {width, height} = buildPreviewViewportSize(
                hostWidth,
                window.innerWidth,
                window.innerHeight,
                pageOrientation,
            );
            setViewportSize((current) => current.width === width && current.height === height ? current : {width, height});
        };

        updateViewportSize();
        const resizeObserver = typeof ResizeObserver !== 'undefined' && viewportHostRef.current
            ? new ResizeObserver(() => updateViewportSize())
            : null;

        if (resizeObserver && viewportHostRef.current) {
            resizeObserver.observe(viewportHostRef.current);
        }

        window.addEventListener('resize', updateViewportSize);
        return () => {
            window.removeEventListener('resize', updateViewportSize);
            resizeObserver?.disconnect();
        };
    }, [open, pageOrientation, previewData?.dataUrl]);

    useEffect(() => {
        if (!canPan && (previewState.imageOffset.x !== 0 || previewState.imageOffset.y !== 0)) {
            updatePreviewState((current) => ({...current, imageOffset: {x: 0, y: 0}}));
            dragStateRef.current = null;
            setIsDragging(false);
        }
    }, [canPan, previewState.imageOffset.x, previewState.imageOffset.y]);

    useEffect(() => {
        if (!previewData?.dataUrl) {
            setImageNaturalSize({width: 0, height: 0});
            setSourceImage(null);
            return;
        }

        let active = true;
        const image = new Image();
        image.onload = () => {
            if (!active) {
                return;
            }
            setSourceImage(image);
            setImageNaturalSize({
                width: image.naturalWidth,
                height: image.naturalHeight,
            });
        };
        image.src = previewData.dataUrl;

        return () => {
            active = false;
        };
    }, [previewData?.dataUrl]);

    const fitRect = useMemo(() => {
        return buildPreviewFitRect(
            viewportSize,
            imageNaturalSize,
            pageOrientation,
            previewState.imageRotation,
        );
    }, [imageNaturalSize.height, imageNaturalSize.width, pageOrientation, previewState.imageRotation, viewportSize.height, viewportSize.width]);

    const drawRect = useMemo(() => {
        return buildPreviewDrawRect(fitRect, previewState.printScale);
    }, [fitRect.height, fitRect.left, fitRect.top, fitRect.width, previewState.printScale]);

    const panBounds = useMemo(() => {
        return buildPreviewPanBounds(drawRect, viewportSize);
    }, [drawRect.height, drawRect.left, drawRect.top, drawRect.width, viewportSize.height, viewportSize.width]);

    const currentPan = useMemo(
        () => getPreviewPanFromOffset(previewState.imageOffset, panBounds),
        [panBounds, previewState.imageOffset],
    );

    useEffect(() => {
        const canvas = previewCanvasRef.current;
        if (!canvas || !sourceImage || !viewportSize.width || !viewportSize.height || !fitRect.width || !fitRect.height) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(viewportSize.width * dpr));
        canvas.height = Math.max(1, Math.round(viewportSize.height * dpr));
        canvas.style.width = `${viewportSize.width}px`;
        canvas.style.height = `${viewportSize.height}px`;

        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.scale(dpr, dpr);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        const drawWidth = drawRect.width;
        const drawHeight = drawRect.height;
        const drawX = drawRect.left + currentPan.x;
        const drawY = drawRect.top + currentPan.y;

        context.save();
        context.translate(drawX + drawWidth / 2, drawY + drawHeight / 2);
        context.rotate((previewState.imageRotation * Math.PI) / 180);

        if (previewState.imageRotation === 90 || previewState.imageRotation === 270) {
            context.drawImage(sourceImage, -drawHeight / 2, -drawWidth / 2, drawHeight, drawWidth);
        } else {
            context.drawImage(sourceImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        }

        context.restore();
        context.strokeStyle = 'rgba(148, 163, 184, 0.75)';
        context.lineWidth = 1.5;
        context.strokeRect(drawX, drawY, drawWidth, drawHeight);
    }, [
        currentPan.x,
        currentPan.y,
        drawRect.height,
        drawRect.left,
        drawRect.top,
        drawRect.width,
        fitRect.height,
        fitRect.width,
        previewState.imageRotation,
        sourceImage,
        viewportSize.height,
        viewportSize.width,
    ]);

    useEffect(() => {
        if (!isDragging || !canPan) {
            return;
        }

        const updatePan = (clientX: number, clientY: number) => {
            if (!dragStateRef.current) {
                return;
            }

            const deltaX = clientX - dragStateRef.current.startX;
            const deltaY = clientY - dragStateRef.current.startY;
            const clampedPan = clampPreviewPan(
                dragStateRef.current.panX + deltaX,
                dragStateRef.current.panY + deltaY,
                panBounds,
            );

            updatePreviewState((current) => ({
                ...current,
                imageOffset: getPreviewOffsetFromPan(clampedPan, panBounds),
            }));
        };

        const handleMouseMove = (event: MouseEvent) => updatePan(event.clientX, event.clientY);
        const handleTouchMove = (event: TouchEvent) => {
            const touch = event.touches[0];
            if (!touch) {
                return;
            }
            updatePan(touch.clientX, touch.clientY);
        };
        const stopDragging = () => {
            dragStateRef.current = null;
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopDragging);
        window.addEventListener('touchmove', handleTouchMove, {passive: true});
        window.addEventListener('touchend', stopDragging);
        window.addEventListener('touchcancel', stopDragging);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopDragging);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', stopDragging);
            window.removeEventListener('touchcancel', stopDragging);
        };
    }, [canPan, isDragging, panBounds.maxX, panBounds.maxY, panBounds.minX, panBounds.minY]);

    const startDragging = (clientX: number, clientY: number) => {
        if (!canPan) {
            return;
        }
        dragStateRef.current = {
            startX: clientX,
            startY: clientY,
            panX: currentPan.x,
            panY: currentPan.y,
        };
        setIsDragging(true);
    };

    return (
        <Stack spacing={2}>
            <Box ref={viewportHostRef} sx={{display: 'flex', justifyContent: 'center'}}>
                <Stack spacing={1.5} sx={{width: viewportSize.width ? `${viewportSize.width}px` : '100%', maxWidth: '100%'}}>
                    <Typography variant="subtitle2">
                        {t('Print Preview')}
                    </Typography>

                    <Box
                        onMouseDown={(event) => startDragging(event.clientX, event.clientY)}
                        onTouchStart={(event) => {
                            const touch = event.touches[0];
                            if (touch) {
                                startDragging(touch.clientX, touch.clientY);
                            }
                        }}
                        sx={{
                            width: `${viewportSize.width}px`,
                            height: `${viewportSize.height}px`,
                            maxWidth: '100%',
                            bgcolor: 'white',
                            boxShadow: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: canPan ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            userSelect: 'none',
                            touchAction: 'none',
                        }}
                    >
                        {previewData && (
                            <canvas
                                ref={previewCanvasRef}
                                aria-label={t('Floorplan')}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                }}
                            />
                        )}
                        <IconButton
                            size="small"
                            onClick={() => {
                                updatePreviewState((current) => ({
                                    ...current,
                                    imageRotation: (current.imageRotation + 90) % 360,
                                    imageOffset: {x: 0, y: 0},
                                }));
                            }}
                            onMouseDown={(event) => event.stopPropagation()}
                            onTouchStart={(event) => event.stopPropagation()}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                bottom: 12,
                                zIndex: 1,
                                width: 30,
                                height: 30,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                boxShadow: 2,
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                },
                            }}
                            title={t('Rotate')}
                        >
                            <ScreenRotationAltIcon sx={{fontSize: 18}}/>
                        </IconButton>
                    </Box>
                </Stack>
            </Box>

            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" sx={{minWidth: 50}}>{t('Scale')}</Typography>
                <Slider
                    value={previewState.printScale}
                    onChange={(_, value) => {
                        updatePreviewState((current) => ({
                            ...current,
                            printScale: value as number,
                        }));
                    }}
                    min={0.5}
                    max={1.5}
                    step={0.05}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                />
                <Typography variant="body2" sx={{minWidth: 45, textAlign: 'right'}}>
                    {Math.round(previewState.printScale * 100)}%
                </Typography>
            </Stack>
        </Stack>
    );
});

PrintPreviewCanvas.displayName = 'PrintPreviewCanvas';

export default PrintPreviewCanvas;
